
import _ from 'lodash';
import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { getModule } from 'vuex-module-decorators';
import caretDownMinor from '@returns-centre/resources/CaretDownMinor.svg';
import { AppModule, CustomerModule, ShopModule, store, TradeInModule } from '@returns-centre/store';

import {
    AddExtraItemModal,
    TradeInLimitReachedModal,
    TradeInMinimumValueModal,
    TradeInSummaryModal,
    TradeInItemAddedModal,
    TradeInSelectProductFromCategoryModal,
    AppAccordion,
    AppHeader,
    AppPage,
    TradeInAddress,
    TradeInConfirmation,
    TradeInOrders,
    TradeInProductRegister,
    TradeInShipping,
    TradeInThankYou,
    ValidationErrors,
    TradeInProductRegistrationQuestionsModal,
    AppLookupButton,
} from '@returns-centre/components';

import VueModal from '@/components/VueModal.vue';
import { FormatDateMixin } from '@returns-centre/mixins';

@Component({
    components: {
        TradeInLimitReachedModal,
        AddExtraItemModal,
        TradeInMinimumValueModal,
        TradeInSummaryModal,
        TradeInItemAddedModal,
        TradeInProductRegistrationQuestionsModal,
        TradeInSelectProductFromCategoryModal,
        TradeInProductRegister,
        AppPage,
        VueModal,
        TradeInAddress,
        TradeInShipping,
        TradeInConfirmation,
        TradeInThankYou,
        TradeInOrders,
        ValidationErrors,
        AppAccordion,
        AppHeader,
        FormatDateMixin,
        AppLookupButton,
    },
})
export default class TradeInWizard extends Vue {
    appModule = getModule(AppModule, store);
    customerModule = getModule(CustomerModule, store);
    tradeInModule = getModule(TradeInModule, store);
    shopModule = getModule(ShopModule, store);

    loading = false;
    errors: object[] | null = null;
    addExtraItemModal = false;
    optionsLoaded = false;
    summary = false;
    caretDownMinor = caretDownMinor;
    moveToNext = false;
    hasProductRegistrationQuestionsModal = false;
    hasTradeInItemsLimitReached: boolean = false;
    tradeInItemsLimitReachedModalOpen: boolean = false;
    hasTradeInItemAddedModal: boolean = false;
    hasTradeInMinimumValueModal: boolean = false;

    selectedTradeInProductRecord: TradeInProductRecord | null = null;

    mounted() {
        if (this.customerModule.returnsPortalShopifyCustomer!.id) {
            this.tradeInModule.setTradeInStep(this.shopModule.shop!.bought_online_active ? 1 : 2);
            this.setAppCustomWidth();
            return;
        }

        if (!this.customerModule.returnsPortalShopifyCustomer!.id) {
            this.tradeInModule.setTradeInStep(this.customerModule.hasVerifiedOrder ? 1 : 2);
            this.setAppCustomWidth();
            return;
        }
    }

    get tradeInWizardCustomHeight() {
        return this.tradeInModule.tradeInStep === 1 ? '90%' : '570px';
    }

    hasTradeInLimitReached(): boolean {
        return this.tradeInModule.hasTradeInItemsLimitReached(this.shopModule.shop!.trade_in_items_limit);
    }

    onTradeInProductRecordSelect(tradeInProductRecord: TradeInProductRecord) {
        this.selectedTradeInProductRecord = tradeInProductRecord;
        this.summary = false;
        this.hasProductRegistrationQuestionsModal = true;
    }

    closeTradeInLimitReachedModal() {
        this.tradeInItemsLimitReachedModalOpen = false;
        this.addExtraItemModal = false;
        this.openSummary();
    }

    setOrderLineItem(val: { [key: string]: OrderLineItem }) {
        //
    }

    openSummary() {
        if (!this.tradeInModule.totalTradeInQty) {
            return;
        }
        this.summary = true;
    }

    get hasNext(): boolean {
        return this.tradeInModule.tradeInStep < this.tradeInModule.tradeInMaxSteps;
    }

    get hasPrev(): boolean {
        const productRegistrationActive =
            !this.shopModule.shop!.bought_online_active && this.shopModule.shop!.product_registration_active;

        if (!this.customerModule.returnsPortalShopifyCustomer!.id && this.tradeInModule.tradeInStep === 2) {
            if (this.customerModule.hasVerifiedOrder) {
                return true;
            }

            return false;
        }

        if (productRegistrationActive) {
            return this.tradeInModule.tradeInStep === 2 ? false : this.tradeInModule.tradeInStep > 2;
        }

        return this.tradeInModule.tradeInStep > 1;
    }

    goPrev() {
        this.tradeInModule.setTradeInStepDisabled(false);
        this.moveToNext = !this.tradeInModule.hasMinimumTradeInPriceMet;
        if (!this.hasPrev) {
            return false;
        }
        this.tradeInModule.reset();
        if (this.tradeInModule.tradeInStep === 3 && !this.shopModule.shop!.product_registration_active) {
            this.tradeInModule.setTradeInStep(1);
        } else {
            this.tradeInModule.setTradeInStep(this.tradeInModule.tradeInStep - 1);
        }
        this.setAppCustomWidth();
    }

    async goToRegister(): Promise<void | boolean> {
        if (!this.validateTradeInItemsLimit(this.tradeInModule.tradeInStep, 'goToRegister')) {
            return false;
        }
        this.moveToNext = !this.tradeInModule.hasMinimumTradeInPriceMet;
        if (!this.hasNext) {
            return false;
        }

        try {
            await this.tradeInModule.createTradeInOrderItems();
            this.tradeInModule.setTradeInStep(this.tradeInModule.tradeInStep + 1);
            this.setAppCustomWidth();
            this.addExtraItemModal = false;
        } catch (e) {
            return false;
        }
    }

    async goNext() {
        this.hasTradeInItemAddedModal = false;

        if (!this.validateTradeInItemsLimit(this.tradeInModule.tradeInStep)) {
            return false;
        }

        this.hasTradeInMinimumValueModal = false;
        if (
            !this.tradeInModule.hasMinimumTradeInPriceMet &&
            this.tradeInModule.tradeInItemsContainStoreCreditProduct(this.tradeInModule.trade_in!)
        ) {
            this.hasTradeInMinimumValueModal = true;
            return;
        }

        if (this.hasNext) {
            this.tradeInModule.tradeInValidate();
            if (!_.isEmpty(this.tradeInModule.validationErrors)) {
                return;
            }
            if (
                !this.tradeInModule.hasMinimumTradeInPriceMet &&
                this.tradeInModule.tradeInItemsContainStoreCreditProduct(this.tradeInModule.trade_in!) &&
                (this.tradeInModule.tradeInStep == 2 ||
                    (this.tradeInModule.tradeInStep == 1 && !this.shopModule.shop?.product_registration_active))
            ) {
                this.hasTradeInMinimumValueModal = true;
                return;
            }

            if (!this.tradeInModule.hasMinimumTradeInPriceMet && this.tradeInModule.tradeInStep < 3) {
                this.hasTradeInMinimumValueModal = true;
                return;
            }

            if (this.tradeInModule.tradeInStep === 3) {
                try {
                    const response = await this.tradeInModule.processTradeInStepAction(this.tradeInModule.tradeInStep);
                    if (response) {
                        this.tradeInModule.setTradeInStep(this.tradeInModule.tradeInStep + 1);
                        this.setAppCustomWidth();
                        this.addExtraItemModal = false;
                        return;
                    }
                } catch (e) {
                    if (!this.shopModule.shop!.product_registration_active) {
                        this.tradeInModule.setTradeInStep(this.tradeInModule.tradeInStep - 2);
                    } else {
                        this.tradeInModule.setTradeInStep(this.tradeInModule.tradeInStep - 1);
                    }
                    this.addExtraItemModal = false;
                    this.setAppCustomWidth();
                    return;
                }
            }

            try {
                const response = await this.tradeInModule.processTradeInStepAction(this.tradeInModule.tradeInStep);
                if (response) {
                    this.tradeInModule.setTradeInStep(this.tradeInModule.tradeInStep + 1);
                }
            } catch {
                if (this.tradeInModule.tradeInStep !== 5) {
                    this.tradeInModule.setTradeInStep(this.tradeInModule.tradeInStep - 1);
                }
            } finally {
                this.addExtraItemModal = false;
                this.setAppCustomWidth();
            }
        }
    }

    async goSkip() {
        if (!this.validateTradeInItemsLimit(this.tradeInModule.tradeInStep)) {
            return false;
        }

        if (
            this.tradeInModule.tradeInStep < 3 &&
            !this.tradeInModule.hasMinimumTradeInPriceMet &&
            this.tradeInModule.tradeInItemsContainStoreCreditProduct(this.tradeInModule.trade_in!)
        ) {
            this.hasTradeInMinimumValueModal = true;
            this.addExtraItemModal = false;
            return;
        }

        if (!this.tradeInModule.hasMinimumTradeInPriceMet && this.tradeInModule.tradeInStep == 1) {
            this.hasTradeInMinimumValueModal = true;
            this.addExtraItemModal = false;
            return;
        }

        if (this.hasNext) {
            this.moveToNext = false;
            this.tradeInModule.tradeInValidate();
            if (!_.isEmpty(this.tradeInModule.validationErrors)) {
                return;
            }
            try {
                const response = await this.tradeInModule.processTradeInStepAction(this.tradeInModule.tradeInStep);
                if (response) {
                    this.tradeInModule.setTradeInStep(this.tradeInModule.tradeInStep + 2);
                    this.setAppCustomWidth();
                }
            } finally {
                this.addExtraItemModal = false;
            }
        }
        this.setAppCustomWidth();
    }

    setAppCustomWidth() {
        const tradeInStep = this.tradeInModule.tradeInStep;
        const tradeInStepWidths = this.tradeInModule.trade_in_step_widths;

        if (tradeInStep > 1) {
            this.appModule.setAppCustomWidth(tradeInStepWidths[tradeInStep as keyof TradeInSteps]);
        } else {
            this.appModule.setAppCustomWidth();
        }
    }

    hasOptions(val: boolean) {
        this.optionsLoaded = val;
    }

    canGoNext() {
        this.hasTradeInMinimumValueModal = false;
        this.moveToNext = !!this.shopModule.shop?.product_registration_active;
    }

    selectedTradeInItemsCount(): number {
        return this.tradeInModule.selectedOrderLineItemsCount + this.tradeInModule.registeredTradeInProductsCount;
    }

    validateTradeInItemsLimit(tradeInStep: number, process: string = ''): boolean {
        const totalSelectedItems = this.selectedTradeInItemsCount();

        const tradeInItemsLimit = this.shopModule.shop!.trade_in_items_limit;
        let limitReached: boolean =
            process == 'goToRegister'
                ? totalSelectedItems >= tradeInItemsLimit
                : totalSelectedItems > tradeInItemsLimit;
        // check if trade-in item max item limit reached
        this.hasTradeInItemsLimitReached = false;
        this.tradeInItemsLimitReachedModalOpen = false;
        if (limitReached && tradeInItemsLimit > 0) {
            this.hasTradeInItemsLimitReached = true;
            this.tradeInItemsLimitReachedModalOpen = true;
            return false;
        }
        if (tradeInStep === 1) {
            this.addExtraItemModal = true;
        }
        return true;
    }
}
